/**
 * EVAS v3 — LeadVal integration contracts (EVAS-136, Phase 1 of the LeadVal epic).
 * Canonical types for everything that crosses the EVAS↔LeadVal boundary or encodes a decision from
 * the integration analysis (docs/leadval/ reports 01–10; plan docs/plans/PLAN_LEADVAL_INTEGRATION.md).
 *
 * Import direction: this module imports from `contracts.ts`, never the reverse — the pieces that core
 * contracts need (LeadvalProcessor, ContactConsent, IdentityConfig, AgentLeadvalConfig, PageContext,
 * FingerprintResult) live THERE, under the visitor-context / agent-params sections.
 *
 * Wire shapes follow LeadVal's shipped contracts as of 2026-08-22 (openapi.yaml + the identity-over-MCP
 * rollout). Readers stay TOLERANT: response schemas type only what EVAS consumes and ignore unknown
 * fields. The lookup default tuning profile is `preliminary` (LeadVal's reserved one-time flip landed
 * 2026-08-21; verified live) — verdict mapping must always be pinned to `resolutionProfileId`.
 */
import { z } from "zod";
import { LeadvalProcessor } from "./contracts.js";
// ---------- company modes (report 08 §1) ----------
/** Who owns login, membership and roles for a company. All three values are implemented from day one
 * (decision 2026-08-21): LOCAL = our accounts (modes 1–2), LEADVAL_SSO = ours authoritative + LeadVal
 * as an additional linked sign-in, LEADVAL = LeadVal authoritative (projection, no local login).
 * Mirrored as the `AuthSource` enum in dataconnect/schema — never diverge. */
export const AuthSource = z.enum(["LOCAL", "LEADVAL_SSO", "LEADVAL"]);
export const DEFAULT_AUTH_SOURCE = "LOCAL";
/** A company's LeadVal DATA connection — orthogonal to `authSource` (report 08 §1/§3b): any company
 * may be connected (Data-only mode) or not. Secret Manager REFS only, never secret values; the DB
 * carries the same refs as nullable Company columns. Presence of `apiKeySecretRef` = connected. */
export const LeadvalConnection = z.object({
    /** LeadVal workspace id (`t_<hex>`), also required for LEADVAL/LEADVAL_SSO auth linkage. */
    workspaceId: z.string().min(1),
    /** GCP Secret Manager ref holding the `val_key__…` ingestion API key. */
    apiKeySecretRef: z.string().min(1),
    /** Secret ref holding the agent MCP client's rotating refresh-token record (consent-once grant).
     * Absent until the company's LeadVal admin has approved the grant.
     *
     * ⚠ NEVER SEND AN OAUTH `prompt` ON THIS CLIENT (EVAS-168 constraint A; report 08 §9.12.8). A forced
     * consent SUPERSEDES the previous authorization: LeadVal's `approveConsent` builds a new one on every
     * approval and the grant record repoints, while a token resolves through the grant id it was minted
     * under. Their measurement — a second authorization with no `prompt` leaves the first token working; a
     * third with `select_account` makes it `401 error_reason=grant_missing`. This grant is exactly the case
     * that cannot survive it: consent-once, long-lived, refreshed by background work with no human present,
     * so the first symptom would be `invalid_grant` on a refresh HOURS after the unrelated interactive flow
     * that caused it. The console's link popup does send `prompt=select_account`, and may only keep doing so
     * because it is a DIFFERENT client (`lv_evas_gcip_code_dev`) and their `grant_store.ts:130-155` keys the
     * record on `(clientId, userId, tenantId)`. Keeping the two clients separate is load-bearing, not
     * tidiness: `LINK_PROVIDER_PARAMS` in the console's `lib/leadval-link-flow.ts` is the other half of this
     * note. */
    mcpGrantSecretRef: z.string().optional(),
});
/** Assemble a `LeadvalConnection` from the nullable Company columns; null = not connected. */
export function leadvalConnectionFromColumns(row) {
    if (!row.leadvalWorkspaceId || !row.leadvalApiKeySecretRef)
        return null;
    return {
        workspaceId: row.leadvalWorkspaceId,
        apiKeySecretRef: row.leadvalApiKeySecretRef,
        mcpGrantSecretRef: row.leadvalMcpGrantSecretRef ?? undefined,
    };
}
// ---------- connection WRITE input (EVAS-158; report 08 Table B row B1) ----------
/** A LeadVal workspace/tenant id: `t_` + 16 lowercase hex. There is one entity — a `tenants` document
 * created at their `server.ts:5777` — and a sub-workspace is the same shape with `parentTenantId` set
 * (report 08 §11.2). */
export const LEADVAL_WORKSPACE_ID_PATTERN = /^t_[0-9a-f]{16}$/;
export function isLeadvalWorkspaceId(value) {
    return typeof value === "string" && LEADVAL_WORKSPACE_ID_PATTERN.test(value);
}
/** True when a string is an ingestion key VALUE rather than a Secret Manager reference. The Company
 * columns hold refs ONLY (report 08 §1), so this is the guard on the one mistake the settings surface
 * invites: pasting the `val_key__…` itself into the ref field would put a live LeadVal credential into
 * Postgres, where no rotation, audit or scrubbing reaches it — and it would still "work", because the
 * ref is only ever read back as an opaque string. Refuse by name instead. */
export function looksLikeLeadvalSecretValue(value) {
    return value.trimStart().startsWith("val_key__");
}
/** The write side of `LeadvalConnection`, and deliberately STRICTER than it.
 *
 * `LeadvalConnection` is a READ projection and this module's stated posture is that readers stay
 * tolerant — so it accepts any non-empty `workspaceId`. Input cannot afford that: `leadvalWorkspaceId`
 * is how every LeadVal mode resolves a company, so a typo is not caught later by anything. It produces
 * a company whose every user is refused `WORKSPACE_NOT_ONBOARDED` at sign-in while the console shows a
 * saved workspace link — the same class of "looks like it worked" failure the `NO_WORKSPACE_LINK` guard
 * exists to prevent (report 08 §9.12.6). Strict in, tolerant out.
 *
 * Refs are `.nullish()` because a workspace link WITHOUT an ingestion key is a legitimate state: Mode 3
 * (LEADVAL_SSO) needs the workspace for auth linkage and never ingests anything. `leadvalConnection-
 * FromColumns` reports that row as "not connected", which is correct — connected means data flows. */
export const LeadvalConnectionInput = z.object({
    workspaceId: z.string().regex(LEADVAL_WORKSPACE_ID_PATTERN),
    apiKeySecretRef: z.string().min(1).nullish(),
    mcpGrantSecretRef: z.string().min(1).nullish(),
});
// ---------- role mapping (report 01 §6.2, fail-closed) ----------
/** LeadVal role → EVAS role. `null` = unknown/unmapped → REFUSE sign-in (fail closed; LeadVal's own
 * published integrator rule is "map anything unrecognised to your least-privileged tier, never infer
 * authority from role_rank" — we go further and deny). `superadmin` maps because LeadVal staff IS
 * EVAS staff (same org) — an agreement between the teams, not something public docs promise. */
export function leadvalRoleToEvas(leadvalRole) {
    switch (leadvalRole) {
        case "superadmin":
            return "SUPER_ADMIN";
        case "admin":
            return "COMPANY_ADMIN";
        case "agent":
        case "procurement":
            return "COMPANY_MANAGER";
        default:
            return null;
    }
}
// ---------- container tags (ingestion spec §4.1; report 02) ----------
/** Platform-constant tag every LeadVal-bound document must carry alongside the tenant tag. */
export const LEADVAL_GLOBAL_CONTAINER_TAG = "global_anonymous_pool";
/**
 * The `containerTags` for a LeadVal-bound document, IN ORDER: the key-derived workspace tag FIRST, the pool
 * tag second (EVAS-236). The only sanctioned way to build the list — the writer and the probe both call it,
 * so the order cannot drift between them again.
 *
 * ⚠ **The order is not cosmetic, and getting it wrong is now a refusal.** The store makes a multi-tag
 * document searchable ONLY under its first tag, and LeadVal's cohort and identity-candidate searches run
 * under the workspace tag — so a pool-first document processed normally and was never compared with the
 * workspace's history, with no error anywhere (measured by LeadVal 2026-09-27; every example they had
 * published was pool-first, and so were we). Since `leadval-00362-nn9` a new session whose first tag is not
 * the workspace tag is refused at fetch: terminal `FAILED`, `errorMessage` beginning `Container tag order
 * violation`, never retried. Their equivalent is `documentContainerTags` in `src/archive/container_tags.ts`.
 */
export function leadvalContainerTags(workspaceTag) {
    return [workspaceTag, LEADVAL_GLOBAL_CONTAINER_TAG];
}
/** Derive the tenant containerTag from a `val_key__<tenantId>__<containerTag>__<entropy>` key.
 * MUST be `slice(2, -1)` — never `split("__")[2]` — per the ingestion spec: the tag itself may
 * contain single underscores. Returns null on anything that is not a well-formed key. Under the
 * shared LeadVal Supermemory account a wrong tag is a CROSS-TENANT READ, not a lookup failure
 * (report 06 §1), so this is the only sanctioned way to obtain a tag. */
export function deriveLeadvalContainerTag(apiKey) {
    const parts = apiKey.split("__");
    if (parts.length < 4 || parts[0] !== "val_key")
        return null;
    const tag = parts.slice(2, -1).join("__");
    return tag.length > 0 ? tag : null;
}
/**
 * Read the TENANT id out of a `val_key__<tenantId>__<containerTag>__<entropy>` key (segment 1).
 *
 * Why this needs to exist (measured on dev, 2026-09-03): LeadVal's API Playground mints for whichever
 * workspace is **currently active**, not one you pick, so it is easy to mint a key for workspace A while
 * intending workspace B — and the resulting key is perfectly valid. Nothing downstream notices, because
 * LeadVal checks that the key's tag is on the DOCUMENT; it never asks whether the tenant matches the
 * company we think we are ingesting for. The session is accepted and the lead is filed under the wrong
 * tenant: a company whose console says it is connected to B while its leads land in A.
 *
 * That comparison is only possible on our side, where both facts sit together — the key's tenant and
 * `Company.leadvalWorkspaceId` — which is why {@link leadvalKeyMatchesWorkspace} is a connector guard
 * rather than a line in a runbook. Returns null on anything that is not a well-formed key or whose
 * tenant segment is not a `t_<16 hex>` workspace id.
 */
export function leadvalTenantIdFromApiKey(apiKey) {
    const parts = apiKey.split("__");
    if (parts.length < 4 || parts[0] !== "val_key")
        return null;
    return isLeadvalWorkspaceId(parts[1]) ? parts[1] : null;
}
/** Does this ingestion key belong to the workspace the company is linked to? `null` workspace = nothing
 * to check against (a company may legitimately hold a key before its workspace link is recorded), so
 * the answer is `true`; an unparseable key is `false`, because a key we cannot read is a key we cannot
 * vouch for. */
export function leadvalKeyMatchesWorkspace(apiKey, workspaceId) {
    const tenantId = leadvalTenantIdFromApiKey(apiKey);
    if (!tenantId)
        return false;
    if (!workspaceId)
        return true;
    return tenantId === workspaceId;
}
// ---------- sessionId projection safety (ingestion spec §15.6; report 02 §6 Q1) ----------
/** LeadVal's session-id uniqueness projection: ids must stay unique AFTER this transform ("sess-1"
 * and "SESS_1" are one session; the second submission is silently never processed). Our session ids
 * are UUIDv4 — 32 unique hex chars survive this by construction. Guard rule: NEVER derive session
 * ids from separator-joined human-readable parts (room names, timestamps); the unit test pins it. */
export function leadvalSessionIdProjection(id) {
    return id.replace(/[^a-zA-Z0-9]/g, "").toUpperCase();
}
// ---------- ingestion notify (POST /api/v1/sessions) ----------
export const LeadvalNotifyRequest = z.object({
    sessionId: z.string().max(128),
    processor: LeadvalProcessor,
});
/** 202 body — idempotent on the PROJECTED session id; duplicates return the full current status with
 * `alreadySubmitted: true`. Tolerant: unknown fields ignored. */
export const LeadvalNotifyResponse = z.object({
    status: z.string().optional(),
    sessionId: z.string().optional(),
    alreadySubmitted: z.boolean().optional(),
    processing: z.object({ state: z.string() }).optional(),
});
/** GET /api/v1/sessions/{id}/status — polled bounded-ly after notify; `rejection`/FAILED alert ops
 * (a MANDATORY-metadata bug on our side silently rejects every lead — report 07 §5). Never carries
 * PII/score/price. */
export const LeadvalSessionStatus = z.object({
    conformance: z
        .object({
        /** CONFORMANT | ACCEPTED_WITH_NOTES (graded) | PENDING (keep polling) | NOT_EVALUATED (terminal —
         * no grade is ever coming). Conflating the last two is how an integration polls for ever. */
        level: z.string(),
        /** Present ONLY on `NOT_EVALUATED`, and the reason to stop: `AUTOMATION_ONLY_NO_DOCUMENT_FETCH`
         * (processor 0 never opens the document) or `NO_CONFORMANCE_REPORT_RECORDED` (the session reached
         * a terminal state first — a container-tag refusal included). EVAS-139 reads it. */
        reason: z.string().optional(),
        /** ABSENT under `PENDING` and present-but-NULL under `NOT_EVALUATED` — an asymmetry worth not
         * branching on; use `level`. */
        evaluatedAt: z.string().nullish(),
        notes: z.array(z.unknown()).optional(),
    })
        .optional(),
    processing: z
        .object({
        state: z.string(), // QUEUED | PROCESSING | PROCESSED | DEGRADED | FAILED
        reprocessingCandidate: z.boolean().optional(),
        waitingForSource: z.boolean().optional(),
        errorMessage: z.string().optional(),
        /** Emitted ONLY for a missing MANDATORY metadata key (never for a tag mismatch). Its presence
         * means no lead exists and none will: the session is not reprocessed after a fix, it must be
         * re-run. Typed loosely — every field optional — because it is an alerting payload, not a
         * control-flow contract. */
        rejection: z
            .object({
            code: z.string().optional(), // INGEST_CONTRACT_VIOLATION
            missingMandatoryKeys: z.array(z.string()).optional(),
            /** The COMPLETE MANDATORY set as their live registry held it AT EVALUATION TIME — added by
             * LeadVal on our ask (Round-14), because the set lives in their `/global_calibration/sla_signals`
             * Firestore document and can therefore widen with **no deploy and no revision to cite**. That is
             * how `processor` became MANDATORY on 2026-08-26 and rejected sessions silently until we found it
             * by reading their commits. Told only which keys they missed, a connector can fix today's
             * rejection and cannot self-check against tomorrow's; {@link unknownMandatoryMetadataKeys} is
             * what closes that.
             *
             * ⚠ An EMPTY array means **"not recorded"**, never "nothing is mandatory" — rejections written
             * before 2026-09-03 carry none, and their own `openapi.yaml` says so at
             * `ProcessingState.rejection.mandatoryKeys`. It is also a SNAPSHOT: do not read an old
             * rejection's set as the current registry. Envelope-borne signals (`content`, the
             * `customId`/`sessionId` agreement) are excluded from it — measured live 2026-09-04, the set is
             * `["started","embedding","processor"]` and `sessionId` is NOT in it. */
            mandatoryKeys: z.array(z.string()).optional(),
            rejectedAt: z.string().optional(),
            message: z.string().optional(),
        })
            .optional(),
        /**
         * ⚠ **A DIAGNOSIS, NOT A VERDICT — never branch on this as a failure.** LeadVal is still *waiting*
         * for a MANDATORY key and the keys usually arrive; a client that gives up here abandons a session
         * that is about to succeed. Emitted only while `state: "QUEUED"` with `waitingForSource: true`, and
         * **cleared on the terminal write** — measured 2026-09-05: present on the first poll, unchanged
         * across 13 consecutive polls, absent the moment `state` became `FAILED`.
         *
         * **The trap is the key names.** `missingMandatoryKeys` and `mandatoryKeys` are the same names
         * {@link LeadvalSessionStatus} carries under `rejection`, which IS terminal. Anything that
         * pattern-matches on those names — or copies the rejection branch — reports a succeeding session as
         * failed. LeadVal's `openapi.yaml` says so in capitals; their machine-readable surfaces do not, which
         * we reported as Round-18 §5.4.
         *
         * **Why we read it at all** (report 02 §14): their contract rejection now takes **~200 s** by design,
         * because their readiness gate used to destroy leads whose metadata arrived late. Our bounded poll is
         * 20 s — it runs inside LiveKit session shutdown — so the verdict is out of reach, and this field is
         * the only thing that lands inside our budget. It turns a silent 212-second loss into a named key.
         */
        awaitingMandatoryKeys: z
            .object({
            missingMandatoryKeys: z.array(z.string()).optional(),
            mandatoryKeys: z.array(z.string()).optional(),
            message: z.string().optional(),
        })
            .optional(),
        /**
         * ⚠ **PAST TENSE. NOT A FAILURE, AND NOT "WAITING NOW" — it is PRESENT ON CONFORMED SESSIONS BY
         * DESIGN, which is the entire reason it exists.** The record that this session had to wait for a
         * MANDATORY key at least once, written OUTSIDE every state branch and never cleared
         * (`../LeadVal/src/ingestion/processing_state.ts:126-139`), so unlike {@link awaitingMandatoryKeys}
         * it survives the terminal write.
         *
         * **The two fields together are the state machine.** BOTH present ⇒ still waiting. Only this one ⇒
         * the wait is over, and `state` says how it ended. Measured on the wire 2026-09-14, one session
         * (report 02 §15): `PROCESSING` with only this one, then three `QUEUED` polls carrying both, then
         * `FAILED` with only this one again. So it appears on states where nothing is waiting and on states
         * where nothing is wrong — anything that treats it as a fault reports a healthy session as broken.
         * This is the {@link awaitingMandatoryKeys} hazard one level on, and worse: the key name no longer
         * contains the word "awaiting" in the present tense, so the *name* is the only guard left.
         *
         * **Why we asked for it** (Round-19 §4, built to our specification): their terminal clear of the
         * present-tense field is load-bearing against a `{merge: true}` resurrection they verified against
         * the live database, so a lead that waited and then CONFORMED stops reporting keys it has since
         * supplied — destroying the record on the only path where it is durable. A connector beginning to
         * lose leads shows up FIRST as sessions that waited and recovered, before any lead is lost. That
         * leading indicator is this field.
         *
         * The block is **OMITTED, not null**, when there was never a wait, and `missingMandatoryKeys` has
         * ≥1 element whenever it is present.
         */
        lastAwaitingMandatoryKeys: z
            .object({
            missingMandatoryKeys: z.array(z.string()).optional(),
            /** ⚠ An EMPTY array means **"not recorded"**, never "nothing is mandatory" — the same
             * semantic as under `rejection`, and the same trap. It is also the THIRD wire source for the
             * MANDATORY set and the only one durable on a conformed session, their own replacement text
             * for the removed `docs/` pointer naming just the other two (Round-20 §5). */
            mandatoryKeys: z.array(z.string()).optional(),
            /** When the last wait was recorded. ⚠ **RE-STAMPED on every re-read** — four distinct values
             * inside one measured session, and only the last survives — so it answers "when did this
             * session last have to wait", NEVER "how many times". Do not present it as a count. */
            observedAt: z.string().nullish(),
            message: z.string().optional(),
        })
            .optional(),
    })
        .optional(),
});
// ---------- identity lookup (POST /api/v1/identity/lookup) ----------
/** Evidence for the lookup. Wire vocabulary is CLOSED — unknown/misnamed keys are 422 with the
 * nearest legal field named (`names` MUST be the object form; the array form was refused as of
 * 2026-08-21). Include the claimed name whenever available: phone-only can never clear any profile's
 * sufficiency floor, while phone+name scores EXACT_MATCH (report 06 §6). Field limits per the lookup
 * spec §2. */
export const LeadvalEvidence = z.object({
    fingerprint: z.record(z.string(), z.unknown()).optional(), // nested ingestion shape or flat
    emails: z.array(z.string().max(254)).optional(),
    phones: z.array(z.string().max(30)).optional(),
    names: z
        .object({
        canonicalFirst: z.string().max(120).optional(),
        canonicalLast: z.string().max(120).optional(),
        claimedFirst: z.string().max(120).optional(),
        claimedLast: z.string().max(120).optional(),
    })
        .optional(),
    voiceSignatures: z
        .array(z.object({ id: z.string().max(128), namespace: z.string().max(64) }))
        .optional(), // NOT emitted in phase 1 (voice deferred — EVAS-130); typed for the wire's sake
    /** ⚠ `latitude`/`longitude` SPELLED OUT — not `lat`/`lon`, which is what this field said until
     * EVAS-140 and which their validator refuses by name: `evidence.coordinates.latitude must be a
     * finite number` (`../LeadVal/src/identity/resolve_api.ts:162-168`, read by
     * `buildWireIdentityEvidence` at `evidence.ts:261-264`). The whole evidence block is a CLOSED
     * vocabulary, so a plausible synonym is a 422 for the entire request rather than one dropped field —
     * i.e. the abbreviation would have cost us the lookup, not the coordinate. Nothing had sent it yet,
     * because Phase 1 typed the wire and Phase 5 is the first caller.
     *
     * We do not in fact emit it (see `buildLookupEvidence` in `@evas/leadval-client`): every co-location
     * channel — coordinates included — is `recordOnly` in every tuning profile since 2026-08-21, so it
     * mints no retrieval token and moves no posterior. It is typed because the wire has it. */
    coordinates: z.object({ latitude: z.number(), longitude: z.number() }).optional(),
    /** ISO-8601 "when this evidence was observed". Omit it: their lookup defaults the current side to
     * `Date.now()`, which is what a live conversation IS, and a wire value WINS over that default
     * (`lookup_run.ts:239-241` + `evidence.ts:286-301`). Sending a slightly stale value would only
     * mis-date the age-gap decay; sending an unparseable one is a 422. */
    timestamp: z.string().optional(),
});
export const LeadvalLookupRequest = z.object({ evidence: LeadvalEvidence });
/** Known verdicts. Handlers MUST have a default branch — the vocabulary can grow (lookup spec §5).
 * Semantics that gate behavior: POSSIBLE_MATCH = do not greet by name; SESSION_FORK = security
 * signal, fetch nothing, log a SessionEvent and skip personalization (decision Q4); device-only
 * evidence can never yield EXACT_MATCH. */
export const LEADVAL_LOOKUP_VERDICTS = [
    "EXACT_MATCH",
    "PROFILE_MERGE_DETECTED",
    "POSSIBLE_MATCH",
    "SESSION_FORK",
    "INSUFFICIENT_EVIDENCE",
    "NEW_PROFILE",
];
/** MAY THE AGENT SPEAK A NAME THE VISITOR HAS NOT GIVEN IT? `matches[].nameUse` is the ONE field that
 * answers it (LeadVal `src/identity/name_use.ts`) — never derive it from verdict/band/`corroboratedBy`
 * + top-level `constraintsFired`, the four-field join it replaced: that union carries clamps belonging
 * to OTHER candidates, so it withheld names we were entitled to use and taught callers to ignore the
 * field. Deliberately two values, so nothing can be rounded toward "probably fine". */
export const LEADVAL_NAME_USE_PERMITTED = "PERMITTED";
/** True only for an EXPLICIT `PERMITTED`. Absent, misspelled, or a value newer than this build all
 * mean withhold — the failure direction matters (greeting a stranger by someone else's name is not
 * recoverable mid-conversation; asking for a name is). */
export function leadvalNamePermitted(match) {
    return match?.nameUse === LEADVAL_NAME_USE_PERMITTED;
}
/** The one `nameUseReason` that needs DIFFERENT agent behaviour from every other WITHHOLD (report 03
 * §5-quinquies; LeadVal `src/identity/scoring.ts:733-806`). Every other reason means "ask something
 * only the real person could answer" — but here the SURNAME already agreed, so repeating it confirms
 * nothing. The useful question is a FIRST name, and a nickname suffices ("Jon" against a stored
 * "Jonathan" lifts the clamp).
 *
 * ⚠ NEVER BUILD AN ENUM FROM THE REASON VOCABULARY. It went five → six → seven values between
 * 2026-08-21 and 2026-09-02, and LeadVal's own spec warns that an enum from an older copy of the line
 * will reject a live value. This is one recognised member, not a closed set. */
export const LEADVAL_NAME_USE_REASON_SHARED_SURNAME = "SHARED_SURNAME_NO_FORENAME";
/** One candidate. ⚠ `matches[]` is ordered BAND-first then posterior (since 2026-08-21) — read
 * `band`, never assume `matches[0]` has the highest posterior. `leadId` is always present (pass to
 * the read-back endpoints); `customerProfileId` is commonly null.
 *
 * ⚠ **NEVER THRESHOLD ON `posterior`.** A household member on a shared landline + PC scores 0.9985
 * against the caller's 1.0 — indistinguishable by any cutoff. Only `nameUse` and the constraints
 * separate them, which is exactly why `nameUse` exists. */
export const LeadvalLookupMatch = z.object({
    leadId: z.string(),
    conversationId: z.string().nullable().optional(),
    customerProfileId: z.string().nullable().optional(),
    processorMode: z.number().nullable().optional(),
    band: z.string(),
    posterior: z.number(),
    corroboratedBy: z.array(z.string()).default([]), // empty = device-only match
    channels: z.array(z.object({ id: z.string(), result: z.string() })).optional(), // Component B
    // ---- additive since 2026-08-22 (report 06 §7); optional because the frozen Phase-1 schema shipped
    // without them and a response from an older server must still parse. Phase 5 consumes all four.
    /** {@link LEADVAL_NAME_USE_PERMITTED} / `"WITHHOLD"`. Read through {@link leadvalNamePermitted}, not
     * as a string comparison, so an absent field fails safe. Typed as a string rather than an enum for
     * the same reason `nameUseReason` is: a value we have not seen must not throw the whole parse away. */
    nameUse: z.string().optional(),
    /** Null EXACTLY when PERMITTED. A constraint id, or `INSUFFICIENT_EVIDENCE` / `BELOW_MATCH_BAND`.
     * See {@link LEADVAL_NAME_USE_REASON_SHARED_SURNAME} for the one value that changes what to ask. */
    nameUseReason: z.string().nullish(),
    /** THIS pair's constraints — not the response-wide union at the top level, which describes candidates
     * this row is not (a clean email-carried EXACT_MATCH was measured beside another candidate's
     * `SHARED_CONTACT_NO_NAME`). */
    constraintsFired: z.array(z.string()).optional(),
    /** ISO-8601 recency of the matched conversation, and the ONLY way to tell a 3-day-old relationship
     * from a 400-day-old one: same-person conversations score identically. "Welcome back" is warm or
     * absurd depending on which this is. Null when the index row predates the field. */
    lastSeenAt: z.string().nullish(),
});
/** A candidate retrieved and found BELOW the bar — or never scored at all. Same row builder as a match on
 * their side (`toRow`, LeadVal `src/identity/lookup_service.ts`), but NOT the same nullability: a pair that
 * never met sufficiency is reported with `band: null` and `posterior: null`, and that null is meaningful —
 * "not enough to judge", not "judged unlikely" (their `LookupMatch` declares `band: string | null`,
 * `posterior: number | null`).
 *
 * ⚠ **This used to be parsed with {@link LeadvalLookupMatch}, whose `band`/`posterior` are required.** One
 * never-scored candidate therefore failed the WHOLE response, and the client reported a perfectly valid `200`
 * as `unavailable` / `UNPARSEABLE` — indistinguishable from an outage, so returning-customer recognition was
 * silently lost on exactly the lookups that retrieved someone below sufficiency. Seen on dev 2026-09-17 and
 * staged as a LeadVal finding before their source showed it was ours. Logging only; nothing branches on it. */
export const LeadvalLookupNearMiss = LeadvalLookupMatch.extend({
    band: z.string().nullish(),
    posterior: z.number().nullish(),
});
/** WHY the verdict is what it is — one pair, the DECIDING one, never an aggregate (sufficiency is a
 * per-pair property, so an average would describe nobody). Typed rather than carried as `unknown`
 * because Phase 5 reads two things out of it: `sufficiency.askFor`, which is what to ask the visitor
 * for when the evidence was too thin, and `clamps`/`activeGuards` for the log. Every field is optional
 * and unknown keys are stripped — their decision block gains fields regularly. */
export const LeadvalLookupDecision = z.object({
    band: z.string().nullish(),
    sufficiency: z
        .object({
        met: z.boolean().optional(),
        comparableChannels: z.number().optional(),
        requiredChannels: z.number().optional(),
        evidenceCapacity: z.number().optional(),
        requiredCapacity: z.number().optional(),
        /** Channel ids that would raise `comparableChannels`. Absent once sufficiency is met. */
        helpfulChannels: z.array(z.string()).optional(),
        /** ⚠ THE SUBSET A PERSON CAN ACTUALLY BE ASKED FOR, as coarse kinds — `name`, `email`, `phone`,
         * `location` (LeadVal `src/idp/mcp_tools.ts:1057`). On a thin lookup this is the actionable half
         * of the answer: ask for exactly these and nothing else, so the visitor is not interrogated. It
         * is the UNION across candidates, not the deciding pair's own, and it is omitted (never empty)
         * when there is nothing to ask for. */
        askFor: z.array(z.string()).optional(),
    })
        .nullish(),
    /** Constraints that held the DECIDING pair down. The top-level `constraintsFired` is the union. */
    clamps: z.array(z.string()).optional(),
    /** Which guards were IN FORCE — the rule set, not the outcome. Distinguishes "no household confusion
     * was detected" from "this workspace's tuning does not look for it". */
    activeGuards: z.array(z.string()).optional(),
    /** The TUNING profile, never a customer profile — same value as `resolutionProfileId`. */
    resolutionProfile: z.string().nullish(),
});
/** The tuning profile a lookup is scored under, and the value {@link LeadvalLookupResponse.shape
 * resolutionProfileId} must carry. LeadVal pins it in code
 * (`../LeadVal/src/identity/lookup_run.ts:87` `LOOKUP_RESOLUTION_PROFILE`) and their reserved one-time
 * flip forensic → preliminary is SPENT (2026-08-21, verified live). It is exported so verdict mapping
 * can be PINNED to it: a silent re-tune moves band floors under us, and the live-contract test in
 * `@evas/leadval-client` fails loudly instead of our ladder quietly changing its mind. */
export const LEADVAL_LOOKUP_RESOLUTION_PROFILE = "preliminary";
/** Lookup response — typed to what EVAS consumes; `evidenceParsed`/`retrieval` stay opaque and `nearMisses`
 * nullable-loose (logging/telemetry only — see {@link LeadvalLookupNearMiss}: this comment said "loose" for a
 * month while the code parsed them as strict matches), while `decision` is typed because Phase 5 reads
 * `askFor` out of it. */
export const LeadvalLookupResponse = z.object({
    verdict: z.string(), // one of LEADVAL_LOOKUP_VERDICTS — or something newer; default-branch it
    // nullish, not optional: the LIVE endpoint sends explicit nulls (measured 2026-08-22 on a
    // NEW_PROFILE response: `"confidence": null`), and early-return paths may null the catalog
    // fields too. `.optional()` alone rejects null — the bug EVAS-129's live verification caught.
    confidence: z.number().nullish(),
    constraintsFired: z.array(z.string()).optional(),
    catalogVersion: z.string().nullish(),
    /** Pin verdict-mapping tests to {@link LEADVAL_LOOKUP_RESOLUTION_PROFILE}. Still `.nullish()`:
     * report 06 §7 recorded that first-visitor early returns null this, and their code no longer does
     * (`lookup_run.ts:363-366` moved the catalog load ABOVE the no-candidate return so Component B's
     * `evidenceParsed` could name channels) — but a tolerant reader costs nothing and the field is
     * theirs to change back. */
    resolutionProfileId: z.string().nullish(),
    candidatesConsidered: z.number().optional(),
    truncated: z.boolean().optional(),
    matches: z.array(LeadvalLookupMatch).default([]),
    decision: LeadvalLookupDecision.nullish(),
    evidenceParsed: z.unknown().optional(),
    retrieval: z.unknown().optional(),
    nearMisses: z.array(LeadvalLookupNearMiss).optional(),
    nearMissesTruncated: z.boolean().optional(),
});
// ---------- unattended read-back (LeadVal identity-over-MCP step 8, shipped 2026-08-21) ----------
// GET /api/v1/leads/{leadId}/context and GET /api/v1/profiles/{profileId} on the same ingestion key,
// `identityApiAccess`-gated, TENANT-scoped, egress role "agent" (pricing-scrubbed). Bodies are
// LeadVal-shaped lead/profile projections that evolve on their side — carried as loose records; the
// personalization prompt consumes them as context, not as a typed structure. `contactAvailability`
// is the one field EVAS branches on (masked ≠ absent).
//
// ⚠ **`WITHHELD_UNPURCHASED` CHANGED MEANING ON 2026-09-07, AND THE CALLER IS
// `agent-core/src/personalization.ts` (`describeLead`) — read this before touching it.** That caller landed
// 2026-09-09, two days AFTER the change, so it is written against the CURRENT meaning and is correct; this
// note used to say "there is no caller yet", which was true when it was written and stopped being true the
// next day. LeadVal's `composeLeadContext` used to re-derive the label as "pay-per-lead AND not paid",
// which labelled leads as withheld whose contact details were in the very same payload; it now reads
// `_entitled`, the flag their egress transform actually acted on. Their own note: *"a payload with contact
// details in it and a label saying they are being withheld. The agent believes the label."* So the value is
// now RARER and genuinely means withheld — a caller written against the old behaviour would under-use
// contact details it is entitled to. Shipped with **no notice entry**, which we raised as Round-18 §5 / the
// Round-19 no-notice list; found by reading their commits, which is the only reason it is written here.
export const LEADVAL_CONTACT_AVAILABILITY = [
    "AVAILABLE",
    "NOT_RECORDED",
    "WITHHELD_UNPURCHASED",
    "WITHHELD_NO_PERMISSION",
];
/**
 * LeadVal's WIRE labels for the two pipeline-DEPTH processor modes — the strings that appear inside a
 * profile projection, not the prose in {@link LEADVAL_PROCESSORS} (which is console-facing and reads
 * "Automation only"). Source: `../LeadVal/src/types/processor.ts` `PROCESSOR_LABELS` 0 and 1.
 *
 * ⚠ **These are NOT lines of business and must never be narrated as one.** They say how deep their
 * pipeline ran, not what the customer was shopping for, and both have appeared in
 * `profile.signals.verticals` — which is a list a prompt builder is tempted to join into a sentence.
 * `agent-core/src/personalization.ts` filters them out for exactly that reason. Modes 2+ are real
 * verticals and are deliberately absent here: their registry grows (mode 3 arrived unannounced, EVAS-151),
 * so this is an EXCLUDE list of two known non-verticals, never an allow-list of the rest.
 *
 * A mode we cannot resolve is NOT in here either, and that is LeadVal's rule too: an unknown mode means
 * "a line of business we cannot name", which is different from "a mode we know is not one" — their legacy
 * documents predating the field read as real estate elsewhere, so dropping them would shrink a real
 * customer's history (`../LeadVal/src/profiles/read_service.ts` `isLineOfBusiness`).
 */
export const LEADVAL_PIPELINE_DEPTH_LABELS = ["AUTOMATION_ONLY", "IDENTITY_ONLY"];
export const LeadvalLeadContext = z.object({ lead: z.record(z.string(), z.unknown()) });
export const LeadvalProfileContext = z.object({ profile: z.record(z.string(), z.unknown()) });
// ---------- error envelope (RFC 9457 problem+json) ----------
/** LeadVal problem responses (`type` base `https://evas.live/problems/…`). Codes EVAS branches on:
 * IDENTITY_API_NOT_ENABLED (403 — treat as feature-off, not an error), MALFORMED_EVIDENCE (422 —
 * integration bug, alert), WRONG_ID_KIND (422), RATE_LIMITED / concurrency 429 (Retry-After; skip
 * personalization for the session, never retry in-window), TENANT_SUSPENDED. NOTE the trap:
 * `INSUFFICIENT_EVIDENCE_FOR_LOOKUP` is a request ERROR (an integration bug — we sent no usable
 * token); `INSUFFICIENT_EVIDENCE` is a normal lookup VERDICT. Never conflate (report 06 §6). */
export const LeadvalProblem = z.object({
    type: z.string().optional(),
    title: z.string().optional(),
    status: z.number().optional(),
    detail: z.string().optional(),
    code: z.string().optional(),
});
//# sourceMappingURL=leadval.js.map